import Swal from 'sweetalert2';

/**
 * Check if user profile is complete with all required fields
 * @param {Object} options - Optional parameters for checking form state
 * @param {Array} options.selectedJobTitles - Current selected job titles in form
 * @param {Array} options.selectedTargetAudience - Current selected target audience in form
 * @param {Array} options.selectedIndustry - Current selected industry in form
 * @param {Array} options.countryCode - Current selected country in form
 * @param {string} options.goal - Current goal value in form
 * @param {string} options.businessName - Current business name in form
 * @param {string} options.businessDescription - Current business description in form
 * @param {string} options.accountType - Account type ("Page" requires business fields)
 * @returns {boolean} - True if profile is complete, false otherwise
 */
export const isProfileComplete = (options = {}) => {
  const userProfile = JSON.parse(localStorage.getItem("userProfile")) || [];
  const profile = userProfile[0] || {};
  
  const {
    selectedJobTitles = null,
    selectedTargetAudience = null,
    selectedIndustry = null,
    countryCode = null,
    goal = null,
    businessName = null,
    businessDescription = null,
    accountType = null
  } = options;
  
  // Get AccountType from localStorage if not provided
  const currentAccountType = accountType || localStorage.getItem("AccountType");
  
  // Check saved profile data OR current form state (in case user hasn't saved yet)
  const hasJobTitle = (profile.JobTitle && profile.JobTitle.trim() !== '') || 
                      (selectedJobTitles && selectedJobTitles.length > 0);
  const hasTargetAudience = (profile.TargetAudience && profile.TargetAudience.trim() !== '') || 
                           (selectedTargetAudience && selectedTargetAudience.length > 0);
  const hasIndustry = (profile.Industry && profile.Industry.trim() !== '') || 
                     (selectedIndustry && selectedIndustry.length > 0);
  const hasCountry = (profile.Country && profile.Country.trim() !== '') || 
                    (countryCode && countryCode.length > 0);
  const hasGoal = (profile.Usergoal && profile.Usergoal.trim() !== '') || 
                 (goal && goal.trim() !== '');
  
  // Business fields are only required for Page AccountType
  let hasBusinessFields = true;
  if (currentAccountType === 'Page') {
    const hasBusinessName = (profile.BusinessName && profile.BusinessName.trim() !== '') || 
                           (businessName && businessName.trim() !== '');
    const hasBusinessDescription = (profile.BusinessDescription && profile.BusinessDescription.trim() !== '') || 
                                  (businessDescription && businessDescription.trim() !== '');
    hasBusinessFields = hasBusinessName && hasBusinessDescription;
  }
  
  return hasJobTitle && hasTargetAudience && hasIndustry && hasCountry && hasGoal && hasBusinessFields;
};

/**
 * Get list of missing profile fields
 * @param {Object} options - Optional parameters for checking form state
 * @param {string} options.accountType - Account type ("Page" requires business fields)
 * @returns {Array} - Array of missing field names
 */
export const getMissingProfileFields = (options = {}) => {
  const userProfile = JSON.parse(localStorage.getItem("userProfile")) || [];
  const profile = userProfile[0] || {};
  const missing = [];
  
  const {
    selectedJobTitles = null,
    selectedTargetAudience = null,
    selectedIndustry = null,
    countryCode = null,
    goal = null,
    businessName = null,
    businessDescription = null,
    accountType = null
  } = options;
  
  // Get AccountType from localStorage if not provided
  const currentAccountType = accountType || localStorage.getItem("AccountType");
  
  // Check saved profile OR current form state
  if ((!profile.JobTitle || profile.JobTitle.trim() === '') && 
      (!selectedJobTitles || selectedJobTitles.length === 0)) {
    missing.push('Job Title');
  }
  if ((!profile.TargetAudience || profile.TargetAudience.trim() === '') && 
      (!selectedTargetAudience || selectedTargetAudience.length === 0)) {
    missing.push('Target Audience');
  }
  if ((!profile.Industry || profile.Industry.trim() === '') && 
      (!selectedIndustry || selectedIndustry.length === 0)) {
    missing.push('Industry');
  }
  if ((!profile.Country || profile.Country.trim() === '') && 
      (!countryCode || countryCode.length === 0)) {
    missing.push('Country');
  }
  if ((!profile.Usergoal || profile.Usergoal.trim() === '') && 
      (!goal || goal.trim() === '')) {
    missing.push('Primary Goal');
  }
  
  // Business fields are only required for Page AccountType
  if (currentAccountType === 'Page') {
    if ((!profile.BusinessName || profile.BusinessName.trim() === '') && 
        (!businessName || businessName.trim() === '')) {
      missing.push('Business Name');
    }
    if ((!profile.BusinessDescription || profile.BusinessDescription.trim() === '') && 
        (!businessDescription || businessDescription.trim() === '')) {
      missing.push('Business Description');
    }
  }
  
  return missing;
};

/**
 * Validate profile and show error message if incomplete
 * @param {Object} options - Optional parameters for checking form state
 * @param {boolean} options.showAlert - Whether to show alert (default: true)
 * @param {Function} options.navigate - Navigation function to redirect to profile hub
 * @returns {boolean} - True if profile is complete, false otherwise
 */
export const validateProfileComplete = (options = {}) => {
  const { showAlert = true, navigate = null, ...formState } = options;
  
  if (!isProfileComplete(formState)) {
    if (showAlert) {
      const missingFields = getMissingProfileFields(formState);
      Swal.fire({
        position: 'center',
        icon: 'warning',
        title: 'Profile Incomplete',
        html: `Please complete your profile preferences before generating content.<br/><br/>
               <strong>Missing fields:</strong><br/>
               ${missingFields.map(field => `• ${field}`).join('<br/>')}<br/><br/>
               Please go to <strong>User Preferences</strong> section (Profile Hub) and fill in all required fields.`,
        showConfirmButton: true,
        confirmButtonText: 'Go to Profile Hub',
        confirmButtonColor: '#2e3192',
      }).then((result) => {
        if (result.isConfirmed && navigate) {
          // Navigate to User Preferences/Profile Hub
          navigate('/user-form');
        }
      });
    }
    return false;
  }
  return true;
};

